import { type MouseEvent, type ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import type { SheetIds } from '@frameio/shared/contexts';
import { Badge, type BadgePlacement, Button2 } from '@frameio/vapor';
import { theme, type ThemeColorsBackground } from '@frameio/vapor-theme';
import { PRIMARY_NAV_COLLAPSED_WIDTH } from '../../constants';
import { useAppSheetButton } from '../../hooks';
import { getAppSheetAwareBadgeBorderColor } from '../../utils/getAppSheetAwareBadgeBorderColor';
import { type usePointerDownLink } from './usePointerDownLink';

type PrimaryNavButtonBadgeProps = {
  show: boolean;
  backgroundColor?: 'notice' | 'error';
  borderSize?: number;
  borderColor?: ThemeColorsBackground;
  content?: string | ReactNode;
  testid?: string;
  placement?: BadgePlacement;
  size?: 16 | 24;
};

type PrimaryNavButtonProps = {
  icon: ReactNode;
  label: string;
  testid: string;
  sheetId?: SheetIds;
  badge?: PrimaryNavButtonBadgeProps;
  current?: boolean | 'page';
  expanded?: boolean;
  onClick?: (evt: MouseEvent) => void;
  containerProps?: ReturnType<typeof usePointerDownLink> | object;
};

export const PrimaryNavButton = ({
  icon,
  label,
  sheetId = null,
  containerProps,
  badge = { show: false },
  testid = 'primary-nav-button',
  expanded = false,
  current = false,
  onClick = undefined,
}: PrimaryNavButtonProps) => {
  const { currentSheetId } = useAppSheetButton(sheetId);

  const badgeBorderColor = useMemo(() => {
    if (badge.borderColor) {
      return badge.borderColor;
    }
    return getAppSheetAwareBadgeBorderColor(currentSheetId);
  }, [badge, currentSheetId]);

  return (
    <PrimaryNavButtonContainer
      aria-current={current}
      aria-expanded={expanded}
      aria-label={label}
      as="button"
      data-testid={testid}
      {...containerProps}
      onClick={(evt: MouseEvent<HTMLElement>) => {
        if (
          containerProps &&
          'onClick' in containerProps &&
          typeof containerProps.onClick === 'function'
        ) {
          containerProps.onClick(evt);
        }
        onClick?.(evt);
      }}
    >
      <IconWrapper>
        <Badge
          backgroundColor={
            badge?.backgroundColor ? badge.backgroundColor : 'error'
          }
          borderColor={badgeBorderColor}
          borderSize={
            typeof badge?.borderSize === 'number' ? badge.borderSize : 2
          }
          content={badge?.content}
          data-testid={badge?.testid}
          isShowing={badge?.show ?? false}
          label="badge"
          placement={badge?.placement ? badge.placement : 'top-right'}
          size={badge?.size ? badge.size : 16}
        >
          {icon}
        </Badge>
      </IconWrapper>
      <LabelWrapper>{label}</LabelWrapper>
    </PrimaryNavButtonContainer>
  );
};

const PrimaryNavButtonContainer = styled(Button2)`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: ${theme.radii[6]};
  box-shadow: none;
  color: ${theme.colors.icon.secondary};
  cursor: pointer;
  display: flex;
  gap: ${theme.spaces[12]};
  justify-content: baseline;
  outline: none;
  overflow: hidden;
  padding: ${theme.spaces[8]};
  transform: scale(1);
  transition-duration: ${theme.transitions.durations[250]};
  transition-property: transform, color, box-shadow, opacity;
  transition-timing-function: ${theme.transitions.eases.primary};
  width: 100%;

  @container primary-nav-button-group (width: 64px) {
    padding-left: 4px;
  }

  &:not([aria-current='page']):not([aria-expanded='true']):hover,
  &:not([aria-current='page']):not([aria-expanded='true']):focus-visible {
    color: ${theme.colors.text.secondary};
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${theme.colors.border.interactive.focused};
  }

  &:active {
    transform: scale(0.95);
  }

  &[aria-current='page'],
  &[aria-expanded='true'] {
    background: ${theme.colors.background.interactive.activated};
    color: ${theme.colors.text.primary};
  }
`;

export const PRIMARY_NAV_BUTTON_ICON_SIZE = 24;

const IconWrapper = styled.div`
  height: ${PRIMARY_NAV_BUTTON_ICON_SIZE}px;
  position: relative;
  width: ${PRIMARY_NAV_BUTTON_ICON_SIZE}px;
`;

const LabelWrapper = styled.div`
  font-size: ${theme.fontSizes[80]};
  font-weight: ${theme.fontWeights.medium};
  opacity: 0;
  text-align: left;
  transition: opacity ${theme.transitions.durations[150]}
    ${theme.transitions.eases.easeOut};
  width: 100%;

  @container primary-nav-button-group (min-width: ${PRIMARY_NAV_COLLAPSED_WIDTH}px) {
    opacity: 1;
  }
`;
